import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, formatDate, openModal, showToastMessage } from 'enable-ui';
import { setShowSaveAllShedule } from 'stores';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import type { Lessons } from 'modules/dashboard/core/type';
import type { Institution } from 'modules/institutions/core/type';

import type { LessonsInClass } from '../type';

import { downloadURI, getTimeLine } from 'helpers';
import { useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { cloneDeep, isArray, isEmpty, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultLessonsInClass } from '../constant';
import { LessonAssessmentType } from '../enum';
import { getLessonInClass } from '../helpers';

export const useLessonsAssessmentsController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id || '';

  const institutionIdRef = useRef(institutionId);

  const [lessonsInClass, setLessonsInClass] = useState<LessonsInClass>(defaultLessonsInClass);

  const [apiCalled, setApiCalled] = useState(false);

  const [selectedLessons, setSelectedLessons] = useState<Record<string, string>>({});

  const [selectedAssessments, setSelectedAssessments] = useState<Record<string, string>>({});

  const lessonSelected = useString('');

  useEffect(() => {
    if (!id) {
      return;
    }

    getListLessonInClass();
  }, [id]);

  useEffect(() => {
    const sessionScrollPosition = JSON.parse(sessionStorage.getItem('scroll-position') || '{}');

    const getObjectLength = (obj: object) => Object.keys(obj).length;

    if (
      apiCalled &&
      getObjectLength(sessionScrollPosition) > 0 &&
      sessionScrollPosition.classId === id
    ) {
      (document.getElementById('content') as Element).scrollTo({
        top: parseInt(sessionScrollPosition.scrollTop),
        behavior: 'smooth',
      });

      sessionStorage.removeItem('scroll-position');
    }
  }, [apiCalled]);

  const getListLessonInClass = async () => {
    const result: {
      unscheduled: Lessons[];
      scheduled: Lessons[];
      totalLearner: number;
      institution: Institution;
    } = await apiCall({
      url: `/classes/${id}/lessons`,
      method: 'GET',
      showError: true,
      isLoading: true,
    });

    if (result) {
      const newData = getLessonInClass(result.unscheduled, result.scheduled, result.totalLearner);

      setLessonsInClass({
        ...newData,
        institution: result?.institution,
      });

      !apiCalled && setApiCalled(true);
      institutionIdRef.current = result?.institution?.id ?? '';
    }
  };

  const onClickAction = async (
    key: 'duplicate' | 'delete' | 'export',
    itemId: string,
    name?: string,
  ) => {
    const handleDuplicate = async () => {
      const result: { id: string } = await apiCall({
        url: `/classes/${id}/duplicate-lesson/${itemId}`,
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        showToastMessage('success', 'Lesson duplicated successfully!');

        getListLessonInClass();
      }
    };

    const handleExport = async () => {
      const result = await apiCall<any, any>({
        url: `/exports/classes/${id}/assessments`,
        method: 'GET',
        isLoading: true,
        showError: true,
        config: {
          responseType: 'blob',
        },
        data: {
          assessment_id: itemId,
        },
      });

      if (result) {
        const url = URL.createObjectURL(result);

        const fileName = `${name}-${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;

        downloadURI(url, fileName);
      }
    };

    switch (key) {
      case 'duplicate':
        handleDuplicate();
        break;

      case 'delete':
        lessonSelected.setValue(itemId);
        openModal(MODAL_KEY.DELETE_LESSON_IN_CLASS);
        break;

      case 'export':
        if (itemId) {
          handleExport();
        }

        break;

      default:
        break;
    }
  };

  const goToLessonAssessmentDetailPage = (
    type: string,
    itemId: string,
    classScheduleId: string,
    classLessonId: string,
  ) => {
    const path =
      type === LessonAssessmentType.ASSESSMENTS ? PATH.ASSESSMENT_DETAIL : PATH.LESSON_DETAIL;
    const element = document.getElementById('content') as Element;

    sessionStorage.setItem(
      'scroll-position',
      JSON.stringify({ classId: id, scrollTop: element.scrollTop }),
    );

    navigationTo(path.replace(':id', itemId), { state: { classScheduleId, classLessonId } });
  };

  const onChangeSelectedLessons = (lesson: string) => () => {
    const newSelected = cloneDeep(selectedLessons);

    if (!isUndefined(newSelected[lesson])) {
      delete newSelected[lesson];
    } else {
      newSelected[lesson] = lesson;
    }

    setSelectedLessons(newSelected);
  };

  const handleAddLesson = async () => {
    const result: boolean = await apiCall({
      url: `/classes/${id}/add-lessons`,
      method: 'PATCH',
      data: {
        lessonIds: Object.keys(selectedLessons),
      },
    });

    if (result) {
      setSelectedLessons({});
      getListLessonInClass();
      showToastMessage('success', 'Added lesson into class successfully!');
      setShowSaveAllShedule([]);
    }
  };

  const handleDelete = async () => {
    if (lessonSelected.value) {
      const result: boolean = await apiCall({
        url: `/classes/${id}/delete-lesson/${lessonSelected.value}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (result) {
        getListLessonInClass();
        closeModal(MODAL_KEY.DELETE_LESSON_IN_CLASS);
        showToastMessage('success', 'Removed lesson successfully!');
      }
    }
  };

  const handleChangeValue = (
    classLessonId: string,
    date: Date,
    startTime: string,
    endTime: string,
  ) => {
    const newLessons = cloneDeep(lessonsInClass);

    let foundItem: Lessons = {
      id: '',
      name: '',
      exercises: [],
      type: { id: 0, name: '', key: '', type: '' },
      totalExercises: 0,
    };

    Object.values(newLessons).forEach((array) => {
      if (Array.isArray(array)) {
        const item = array.find((el) => el.classLessonId === classLessonId);

        if (item) {
          foundItem = item;

          return; // Exit the loop if item is found
        }
      }
    });

    if (!foundItem) {
      return;
    }

    const currentDate = new Date(date);

    foundItem.start = !startTime
      ? new Date(currentDate.setHours(8)).toISOString()
      : getTimeLine(date, startTime).toISOString();
    foundItem.end = !endTime
      ? new Date(currentDate.setHours(10)).toISOString()
      : getTimeLine(date, endTime).toISOString();

    setLessonsInClass(newLessons);
  };

  const handleUpdateLessonInClass = async (
    classLessonId: string,
    classScheduleId: string,
    start: string,
    end: string,
  ) => {
    let result: any;

    if (classScheduleId) {
      result = await apiCall({
        url: `/class-schedules/${classScheduleId}`,
        method: 'PATCH',
        data: {
          start,
          end,
        },
        isLoading: true,
        showError: true,
      });
    } else {
      result = await apiCall({
        url: `/class-schedules`,
        method: 'POST',
        data: {
          data: [
            {
              classLessonId,
              start,
              end,
            },
          ],
        },
        isLoading: true,
        showError: true,
      });
    }

    if (result?.id || result?.length) {
      showToastMessage('success', 'Updated schedule successfully!');

      getListLessonInClass();
    }
  };

  const onChangeSelectedAssessments = (assessment: string) => () => {
    const newSelected = cloneDeep(selectedAssessments);

    if (!isUndefined(newSelected[assessment])) {
      delete newSelected[assessment];
    } else {
      newSelected[assessment] = assessment;
    }

    setSelectedAssessments(newSelected);
  };

  const handleAddAssessment = async () => {
    const result: boolean = await apiCall({
      url: `/classes/${id}/add-lessons`,
      method: 'PATCH',
      data: {
        assessmentIds: Object.keys(selectedAssessments),
      },
    });

    if (result) {
      setSelectedAssessments({});
      getListLessonInClass();
      showToastMessage('success', 'Added assessment into class successfully!');
      setShowSaveAllShedule([]);
    }
  };

  const onExportClassAssessments = async () => {
    const result = await apiCall<any, any>({
      url: `/exports/classes/${id}/assessments`,
      method: 'GET',
      isLoading: true,
      showError: true,
      config: {
        responseType: 'blob',
      },
    });

    if (result) {
      const url = URL.createObjectURL(result);
      const fileName = `class-assessments-${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;

      downloadURI(url, fileName);
    }
  };

  const onSaveAllToBeScheduled = async (data: Lessons[]) => {
    const newData = data.filter((el) => el.start && el.end);

    if (isEmpty(newData)) {
      return;
    }

    const request = newData.map((el) => ({
      classLessonId: el.classLessonId ?? '',
      start: el.start ?? '',
      end: el.end ?? '',
    }));

    const response: { id: string }[] = await apiCall({
      url: `/class-schedules`,
      method: 'POST',
      data: {
        data: request,
      },
      isLoading: true,
      showError: true,
    });

    if (!isArray(response)) {
      return;
    }

    showToastMessage('success', SUCCESS_MESSAGE.UPDATED_SCHEDULES);

    getListLessonInClass();

    setShowSaveAllShedule([]);
  };

  return {
    onClickAction,
    goToLessonAssessmentDetailPage,
    lessonsAndAssessments: lessonsInClass,
    selectedLessons,
    onChangeSelectedLessons,
    handleAddLesson,
    handleDelete,
    handleUpdateLessonInClass,
    handleChangeValue,
    selectedAssessments,
    onChangeSelectedAssessments,
    handleAddAssessment,
    onExportClassAssessments,
    onSaveAllToBeScheduled,
    institutionId: institutionIdRef.current,
  };
};
